import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@premotec/pmt-essentials';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { HeaderStateService } from '../../../../main/headers/services/header-state.service';
import { DateHelper } from '../../../../shared/helpers/date.helper';
import { AssignmentScheduleOptionAnswerEnum } from '../../enums/assignment-schedule-option-answer.enum';
import { AssignmentScheduleStateService } from '../../services/assignment-schedule-state.service';
import { AssignmentScheduleAnswerAdditionModalComponent } from '../assignment-schedule-answer-addition-modal/assignment-schedule-answer-addition-modal.component';
import { AssignmentScheduleAnswerFilterModalComponent } from '../assignment-schedule-answer-filter-modal/assignment-schedule-answer-filter-modal.component';
import { AssignmentScheduleOptionModel } from '../../models/assignment-schedule-option.model';
import { AssignmentScheduleService } from '../../services/assignment-schedule.service';
import { Subscription } from 'rxjs';
import { AssignmentScheduleMassUpdateAnswerPostModel } from '../../models/assignment-schedule-mass-update-answer-post.model';
import { SharedToasterService } from '../../../../shared/services/shared-toaster.service';

@Component({
    templateUrl: './assignment-schedule-answer.component.html',
    styleUrl: './assignment-schedule-answer.component.scss'
})
export class AssignmentScheduleAnswerComponent extends BaseComponent implements OnInit, OnDestroy {

    private bsModalService = inject(BsModalService);
    private assignmentScheduleService = inject(AssignmentScheduleService);
    private sharedToasterService = inject(SharedToasterService);
    protected assignmentScheduleStateService = inject(AssignmentScheduleStateService)
    protected dateHelper = inject(DateHelper)
    protected assignmentScheduleId!: string;

    protected assignmentScheduleOptionAnswerEnum = AssignmentScheduleOptionAnswerEnum;

    private subscription: Subscription | null = null;

    constructor(
        private route: ActivatedRoute,
        private headerStateService: HeaderStateService,
    ) {
        super();
        this.setHeaderInformation(null, null);
    }

    public ngOnInit(): void {
        this.registerEvents();
        this.route.params.subscribe(params => {
            this.assignmentScheduleId = params['id'];
            this.assignmentScheduleStateService.loadAssignmentScheduleAnswers(this.assignmentScheduleId);
        });
    }

    public override ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        super.ngOnDestroy();
    }

    protected massMutationYes(): void {
        this.massMutation(AssignmentScheduleOptionAnswerEnum.yes);
    }

    protected massMutationMaybe(): void {
        this.massMutation(AssignmentScheduleOptionAnswerEnum.maybe);
    }

    protected massMutationNo(): void {
        this.massMutation(AssignmentScheduleOptionAnswerEnum.no);
    }

    private massMutation(answer: AssignmentScheduleOptionAnswerEnum): void {
        this.assignmentScheduleStateService.disableAll();

        var detailsValue = this.assignmentScheduleStateService.assignmentScheduleDetailsSubject.value;
        if (detailsValue == null) return;
        var options = this.getFilteredOptions(detailsValue.options);
        var eventLessonIds = options.map(x => x.eventLessonId);

        var model: AssignmentScheduleMassUpdateAnswerPostModel = {
            eventLessonIds: eventLessonIds,
            answer: answer
        };

        this.assignmentScheduleService.massUpdateAssignmentScheduleAnswers(this.assignmentScheduleId, model).subscribe({
            next: () => {
                this.sharedToasterService.showGeneralSavingSuccess();
                this.assignmentScheduleStateService.loadAssignmentScheduleAnswers(this.assignmentScheduleId);
            },
            error: () => {
                this.sharedToasterService.showGeneralSavingError();
                this.assignmentScheduleStateService.loadAssignmentScheduleAnswers(this.assignmentScheduleId);
            }
        });


    }


    protected getFilteredOptions(options: AssignmentScheduleOptionModel[]): AssignmentScheduleOptionModel[] {
        if (!this.assignmentScheduleStateService.isAnyFilterDisplayed) return options;
        const filter = this.assignmentScheduleStateService.assignmentSchedulesFilterSubject.value;

        if (filter == null) return options;

        if (filter.facilityIds != null && filter.facilityIds.length) {
            const selected = new Set(filter.facilityIds.map(Number));
            options = options.filter(x =>
                (x.facilityIds ?? []).some(id => selected.has(Number(id)))
            );
        }

        if (filter.levelIds && filter.levelIds.length > 0) {
            const selected = new Set(filter.levelIds.map(Number));
            options = options.filter(x => x.levelId != null && selected.has(Number(x.levelId)));
        }

        if (filter.myAnswerIds && filter.myAnswerIds.length > 0) {
            const selected = new Set(filter.myAnswerIds.map(Number));
            options = options.filter(x => selected.has(Number((x.answer ?? 0 as number))));
        }

        if (filter.isWeekdayFilterActive && filter.weekdays) {
            const activeWeekdays: number[] = [];
            if (filter.weekdays.isMondayActive) activeWeekdays.push(1);
            if (filter.weekdays.isTuesdayActive) activeWeekdays.push(2);
            if (filter.weekdays.isWednesdayActive) activeWeekdays.push(3);
            if (filter.weekdays.isThursdayActive) activeWeekdays.push(4);
            if (filter.weekdays.isFridayActive) activeWeekdays.push(5);
            if (filter.weekdays.isSaturdayActive) activeWeekdays.push(6);
            if (filter.weekdays.isSundayActive) activeWeekdays.push(7);

            if (activeWeekdays.length > 0 && activeWeekdays.length < 7) {
                const active = new Set(activeWeekdays);
                options = options.filter(x =>
                    this.assignmentScheduleService
                        .getWeekdayRange(x)
                        .some(d => active.has(d))
                );
            }
        }

        return options;
    }

    protected openEditAdditionModal(): void {
        var assignmentScheduleDetails = this.assignmentScheduleStateService.assignmentScheduleDetailsSubject.value;
        if (assignmentScheduleDetails == null) return;

        const initialState = {
            assignmentScheduleId: this.assignmentScheduleId,
            comment: assignmentScheduleDetails.comment,
            wishCount: assignmentScheduleDetails.countWish,
            isWishCountActive: assignmentScheduleDetails.isCountWishActive
        };
        const modalRef: BsModalRef = this.bsModalService.show(AssignmentScheduleAnswerAdditionModalComponent, {
            ignoreBackdropClick: true,
            initialState
        });

        modalRef?.content.onSuccess.subscribe((isSuccess: boolean) => {
            if (isSuccess) this.assignmentScheduleStateService.loadAssignmentScheduleAnswers(this.assignmentScheduleId);
        });
    }

    private setHeaderInformation(title: string | null, subtitle: string | null): void {
        this.headerStateService.setDetailHeader(
            title ?? '',
            subtitle ?? '',
            '/assignments/schedules',
            'fa-solid fa-filter text-primary');
    }

    private registerEvents(): void {
        // Header icon action
        this.headerStateService.getActionClickedEvent().subscribe((x) => {
            const modalRef: BsModalRef = this.bsModalService.show(AssignmentScheduleAnswerFilterModalComponent, {
                ignoreBackdropClick: true,
                initialState: { assignmentScheduleId: this.assignmentScheduleId }
            });
        });

        this.subscription = this.assignmentScheduleStateService.assignmentScheduleDetailsSubject.subscribe((model) => {
            this.setHeaderInformation(model?.sportName ?? '', model?.name ?? '');
            if (model == null) return;
            const filter = this.assignmentScheduleStateService.assignmentSchedulesFilterSubject.value;
            if (filter == null) return;

            if (model.id == filter.assignmentScheduleId) return;

            this.assignmentScheduleStateService.resetAllItems();
        });

        this.assignmentScheduleStateService.getIsAnyFilterDisplayedEvent().subscribe(x => {
            this.headerStateService.updateIsActiveStatusIndicatorDisplayed(x);
        });
    }
}
